import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Dialog as D } from 'radix-ui';
import { ArrowRight, Box, CornerDownRight, Building2, FileKey, FileText, FolderKanban, KeyRound, LifeBuoy, Receipt, Search, Server, User, UserRound } from 'lucide-react';
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useCreateActions } from '@/app/create-actions';
import { NAV } from '@/app/nav';
import { Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCanSee } from '@/modules/subscription/api';
import { useCreateLauncher } from './new-menu';

interface Hit {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  href: string;
}

const ICONS: Record<string, ReactNode> = {
  organization: <Building2 />,
  contact: <User />,
  ticket: <LifeBuoy />,
  configuration: <Server />,
  password: <KeyRound />,
  vault_key: <FileKey />,
  document: <FileText />,
  flexible_asset: <Box />,
  employee: <UserRound />,
  invoice: <Receipt />,
  project: <FolderKanban />,
};
const GROUPS: Record<string, string> = {
  organization: 'Organizations',
  contact: 'Contacts',
  ticket: 'Tickets',
  configuration: 'Configurations',
  password: 'Passwords',
  vault_key: 'Keys & certificates',
  document: 'Documents',
  flexible_asset: 'Assets',
  employee: 'People',
  invoice: 'Invoices',
  project: 'Projects',
};

interface PaletteCommand {
  key: string;
  label: string;
  /** Words the query is matched against (label, module, keywords). */
  words: string;
  icon: ComponentType;
  run: () => void;
}

/** Every word of the query starts a word of the command ("new tic" finds "New ticket"). */
const matches = (q: string, words: string) => {
  const hay = words.toLowerCase().split(/[\s›()]+/);
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.some((h) => h.startsWith(w)));
};

/**
 * ⌘K: jump anywhere. With no query it lists the pages and the New commands; as you type it
 * narrows those (sub-pages included, P2-0 N7) and searches the API. Pages and commands use the
 * sidebar's and the New menu's own filters (`useCanSee`), so Platform shows only to platform
 * admins (BUG-58) and nothing the plan leaves out appears.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState('');
  const navigate = useNavigate();
  const canSee = useCanSee();
  const creates = useCreateActions();
  const { launch, element: launcher } = useCreateLauncher();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);
  // Clear the query when the palette closes (adjusting state during render, not in an effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) setQ('');
  }

  const search = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => api<{ results: Hit[] }>('/search', { query: { q: debounced }, signal }),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });

  const go = (href: string) => {
    onOpenChange(false);
    void navigate(href);
  };
  const pages: (PaletteCommand & { top: boolean })[] = NAV.flatMap((g) => g.items.filter(canSee).map((i) => ({ ...i, section: g.section ?? '' })))
    .flatMap((i) => [
      { key: i.to, label: i.label, words: `${i.label} ${i.section}`, icon: i.icon, run: () => go(i.to), top: true },
      ...(i.pages ?? []).filter(canSee).map((p) => ({ key: p.to, label: `${i.label} › ${p.label}`, words: `${i.label} ${p.label}`, icon: CornerDownRight, run: () => go(p.to), top: false })),
    ])
    .filter((p) => (debounced ? matches(debounced, p.words) : p.top))
    .sort((a, b) => Number(b.top) - Number(a.top)); // modules before their sub-pages
  const commands: PaletteCommand[] = creates
    .map((a) => ({
      key: `new:${a.key}`,
      label: `New ${a.label.charAt(0).toLowerCase()}${a.label.slice(1)}`,
      words: `new create add ${a.label} ${a.group} ${(a.keywords ?? []).join(' ')}`,
      icon: a.icon,
      run: () => {
        onOpenChange(false);
        launch(a);
      },
    }))
    .filter((c) => !debounced || matches(debounced, c.words));
  const groups = (search.data?.results ?? []).reduce<Record<string, Hit[]>>((acc, h) => {
    (acc[h.type] ??= []).push(h);
    return acc;
  }, {});

  // The lists are filtered here, not by cmdk, so keep the highlight on a visible row (the first
  // one after the query changes), or Enter does nothing.
  const shownPages = pages.slice(0, debounced ? 8 : undefined);
  const shownCommands = commands.slice(0, debounced ? 6 : undefined);
  const values = [
    ...shownPages.map((p) => `page:${p.key}`),
    ...shownCommands.map((c) => c.key),
    ...(debounced.length >= 2 ? Object.values(groups).flatMap((hits) => hits.map((h) => `${h.type}-${h.id}`)) : []),
  ];
  const highlighted = values.includes(selected) ? selected : (values[0] ?? '');

  const groupClass = '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-subtle';
  const item = 'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm data-[selected=true]:bg-surface-hover [&_svg]:size-4 [&_svg]:text-subtle';
  return (
    <>
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-slate-950/40" />
        <D.Content className="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-surface shadow-pop">
          <D.Title className="sr-only">Search</D.Title>
          <D.Description className="sr-only">Search across Denwen MSP or jump to a page</D.Description>
          <Command shouldFilter={false} value={highlighted} onValueChange={setSelected} label="Search">
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="size-4 text-subtle" />
              <Command.Input value={q} onValueChange={setQ} autoFocus placeholder="Search or jump to…" className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-subtle" />
              {search.isFetching ? <Spinner className="text-subtle" /> : null}
            </div>
            <Command.List className="max-h-[min(60vh,calc(88dvh-6rem))] overflow-y-auto overscroll-contain p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted">{debounced.length >= 2 ? (search.isFetching ? 'Searching…' : 'No results') : 'Type at least two characters to search'}</Command.Empty>
              {pages.length ? (
                <Command.Group heading="Go to" className={groupClass}>
                  {shownPages.map((p) => (
                    <Command.Item key={p.key} value={`page:${p.key}`} onSelect={p.run} className={item}>
                      <p.icon />
                      {p.label}
                      <ArrowRight className="ml-auto opacity-0 [[data-selected=true]_&]:opacity-100" />
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {commands.length ? (
                <Command.Group heading="Create" className={groupClass}>
                  {shownCommands.map((c) => (
                    <Command.Item key={c.key} value={c.key} onSelect={c.run} className={item}>
                      <c.icon />
                      {c.label}
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {debounced.length >= 2
                ? Object.entries(groups).map(([type, hits]) => (
                    <Command.Group key={type} heading={GROUPS[type] ?? type} className={groupClass}>
                      {hits.map((h) => (
                        <Command.Item key={`${h.type}-${h.id}`} value={`${h.type}-${h.id}`} onSelect={() => go(h.href)} className={item}>
                          {ICONS[h.type] ?? <ArrowRight />}
                          <span className="min-w-0 flex-1 truncate">{h.title}</span>
                          {h.subtitle ? <span className="max-w-[45%] truncate text-xs text-subtle">{h.subtitle}</span> : null}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ))
                : null}
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
    {launcher}
    </>
  );
}
